import { useEffect, useState } from "react";
import TablePopover from "./TablePopover";
import MenuIcon from "../../image/hamburgerDotMenu.svg";
import Cookies from "js-cookie";
import Badge from "../badge/Badge";
import { formatCapitalize } from "../../config/utilities/formatCapitalize";
import Text from "../text/Text";
import {
  formatDateTime,
  formatTo12HourTime,
  formatETATime,
} from "../../config/utilities/fDate";
import DragIcon from "./img/drag.svg";
import { useAuth } from "../../config/utilities/useAuth";
import { addPlusPhone } from "../../config/utilities/formatPhone";
import Checkbox from "../checkbox/Checkbox";
import AlightPermissionIcon from "./img/alightPermission.svg";
import CustomTimePicker from "../timePicker/TimePicker";
import {
  getAuditDetailParts,
} from "../../config/utilities/auditLogHelpers";
import Dropdown from "../dropdown/Dropdown";
import ClockIcon from "../../pages/serviceAnnouncement/img/clock.svg";
import ArrowIcon from "../../pages/serviceAnnouncement/img/arrow.svg";
import StarIcon from "../../pages/serviceAnnouncement/img/star.svg";
import InfoIcon from "../../pages/serviceAnnouncement/img/info.svg";
import AlertIcon from "../../pages/serviceAnnouncement/img/alert.svg";
import CheckIcon from "../../pages/serviceAnnouncement/img/checkMark.svg";
import EmailSentIcon from "./img/email-sent.png";
import EmailHoldIcon from "./img/hold-email.png";
import EmailPendingIcon from "./img/pending-email.png";
import EmailFailedIcon from "./img/failed-email.png";
import WarningYellowIcon from "./img/warningYellow.svg";
import { useTimezone } from "../../config/utilities/useTimezone";
import { getTripBadgeProps } from "../../config/utilities/tripBadge";
import FamilyChildCard from "./FamilyChildCard";
import { APP_TOOLTIP_ID, APP_ERROR_TOOLTIP_ID } from "../tooltip/tooltipIds";
import DispatchOccupancyCell from "./DispatchOccupancyCell";
import {
  formatDispatchTripStatus,
  getDispatchTripStatusBadgeProps,
} from "../../config/utilities/dispatchOccupancyHelpers";
import { formatCampaignAudienceSummary, inferCampaignTargetAudience } from "../../config/utilities/campaignAudiencePayload";
import { getAudienceLabel } from "../../pages/notifications/campaignFormHelpers";

const TRIP_TYPE_MISMATCH_TOOLTIP_HTML =
  "<span>This trip type no longer exists for the institution.</span>";

const TARGET_AUDIENCE_SUMMARY_MAX = 48;

const getTargetAudienceDetailLine = (typeLabel, summary) => {
  if (!summary || summary === typeLabel) return null;
  if (summary.startsWith(`${typeLabel}:`)) {
    return summary.slice(typeLabel.length + 1).trim();
  }
  return summary;
};

const renderTargetAudienceCell = (row) => {
  const summary = formatCampaignAudienceSummary(row) || "—";
  const audienceType = inferCampaignTargetAudience(row);
  const typeLabel = getAudienceLabel(audienceType) || summary;
  const detail = getTargetAudienceDetailLine(typeLabel, summary);
  const tooltipText = detail && detail.length > TARGET_AUDIENCE_SUMMARY_MAX
    ? detail
    : summary.length > TARGET_AUDIENCE_SUMMARY_MAX
      ? summary
      : null;
  const detailText = detail
    ? detail.length > TARGET_AUDIENCE_SUMMARY_MAX
      ? `${detail.slice(0, TARGET_AUDIENCE_SUMMARY_MAX)}…`
      : detail
    : null;

  const content = (
    <>
      <Text
        text={typeLabel}
        size="0.875rem"
        weight="500"
        height="1.25rem"
      />
      {detailText ? (
        <Text text={detailText} size="0.75rem" weight="400" color="#667085" />
      ) : null}
    </>
  );

  if (!tooltipText) return content;

  return (
    <div
      data-tooltip-id={APP_TOOLTIP_ID}
      data-tooltip-html={`<span>${tooltipText}</span>`}
      style={{ maxWidth: "14rem", cursor: "default" }}
    >
      {content}
    </div>
  );
};

const getFamilyAccountTypeDisplay = (raw) => {
  const s = String(raw ?? "").trim();
  if (!s) return "Unspecified";
  return s
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

const getFamilyAccountTypeBadgeColors = (normalizedKey) => {
  switch (normalizedKey) {
    case "trial":
      return { background: "#FFFAEB", color: "#B54708" };
    case "standard":
      return { background: "#EFF8FF", color: "#175CD3" };
    case "premium":
      return { background: "#F4F3FF", color: "#5925DC" };
    case "restricted":
      return { background: "#FEF3F2", color: "#B42318" };
    default:
      return { background: "#F2F4F7", color: "#667085" };
  }
};

/** True when subscription is past validUntil but still before graceEndsAt, or API marks grace phase. */
const isFamilySubscriptionInGracePeriod = (sub) => {
  if (!sub || typeof sub !== "object") return false;
  if (sub.inGracePeriod === true || sub.isInGracePeriod === true) return true;
  const phase = String(sub.phase || "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  if (
    phase === "grace" ||
    phase === "graceperiod" ||
    phase === "gracing"
  ) {
    return true;
  }
  const { validUntil, graceEndsAt } = sub;
  if (!validUntil || !graceEndsAt) return false;
  const now = Date.now();
  const paidThrough = new Date(validUntil).getTime();
  const graceEnd = new Date(graceEndsAt).getTime();
  if (Number.isNaN(paidThrough) || Number.isNaN(graceEnd)) return false;
  return now > paidThrough && now <= graceEnd;
};

const resolveTripTypeDisplayName = (tripLike = {}, matchedTypeName) => {
  const candidates = [
    tripLike?.triptypeName,
    tripLike?.tripTypeName,
    tripLike?.typeName,
    tripLike?.tripType?.name,
    tripLike?.tripTypeLabel,
    matchedTypeName,
    tripLike?.type,
    tripLike?.tripType,
  ];

  const resolved = candidates.find(
    (value) => typeof value === "string" && value.trim().length > 0,
  );

  return resolved ? resolved.trim() : "";
};

export default function TableRow({
  row,
  rowId,
  columns,
  onPopoverToggle,
  openPopover,
  handlePopoverAction,
  module,
  draggable = false,
  listeners,
  attributes = {},
  setNodeRef = null,
  style = {},
  onRowChange,
  onRowClick = false,
  isRowClickable,
}) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const { privilege, tripTypes } = useAuth();
  const { timezone } = useTimezone();
  const role = privilege?.role;

  const truncateText = (text, maxLength = 50) => {
    if (!text) return "";

    // Strip HTML tags if it's rich text
    const plainText = text.replace(/<[^>]+>/g, "");

    return plainText.length > maxLength
      ? plainText.slice(0, maxLength) + "…"
      : plainText;
  };

  const getChildStopsFromRow = (row) => {
    if (row.stops?.length) {
      return row.stops.map((stop) => ({
        name: stop.name,
        session: stop.session || "none",
      }));
    }

    if (row.stopData?.length) {
      return row.stopData.map((stop) => ({
        name: stop.stopName,
        session: stop.tripSession || "none",
      }));
    }

    return [];
  };

  const getBadgeProps = (role) => {
    switch (role) {
      case "ADMIN":
        return { background: "#eaf3fb", color: "#175ad0" };
      case "EDITOR":
        return { background: "#d0dfe8", color: "#263f4e" };
      case "VIEWER":
        return { background: "#45748C", color: "#fff" };
      case "SUPERADMIN":
        return { background: "#F0F9FF", color: "#026AA2" };
      case "Scheduled":
        return { background: "#FFFAEB", color: "#B54708" };
      case "On Demand":
        return { background: "#EFF8FF", color: "#175CD3" };
      case "Published":
        return { background: "#ECFDF3", color: "#027A48" };
      case "active":
        return { background: "#ECFDF3", color: "#027A48" };
      case "inactive":
        return { background: "#FEF3F2", color: "#B42318" };
      case "driver":
        return { background: "#FFFAEB", color: "#B54708" };
      case "assistant":
        return { background: "#EFF8FF", color: "#175CD3" };
      case "Exempted":
        return { background: "#F8F8FC", color: "#363F72" };
      case "pickUp":
        return { background: "#EFF8FF", color: "#175CD3" };
      case "dropOff":
        return { background: "#ECFDF3", color: "#027A48" };
      case "failedToBoard":
        return { background: "#FEF3F2", color: "#B42318" };
      case "Delayed":
        return { background: "#FEF3F2", color: "#B42318" };
      case "Arrived":
        return { background: "#ECFDF3", color: "#027A48" };
      case "Ontime":
        return { background: "#EFF8FF", color: "#175CD3" };
      case "PRIMARY":
        return { background: "#EFF8FF", color: "#175CD3" };
      case "SECONDARY":
        return { background: "#FFFAEB", color: "#B54708" };
      case "Ongoing":
        return { background: "#EFF8FF", color: "#175CD3" };
      case "Completed":
        return { background: "#ECFDF3", color: "#027A48" };
      case "CREATE":
        return { background: "#ECFDF3", color: "#027A48" };
      case "UPDATE":
        return { background: "#EFF8FF", color: "#175CD3" };
      case "DELETE":
        return { background: "#FEF3F2", color: "#B42318" };
      default:
        return { background: "#F2F4F7", color: "#344054" };
    }
  };

  const renderCellData = (column) => {
    switch (module) {
      case "admin":
        return renderAdminCell(column, row);
      case "family":
        return renderFamilyCell(column, row);
      case "child":
        return renderChildCell(column, row);
      case "parent":
        return renderParentCell(column, row);
      case "notifications":
        return renderNotificationCell(column, row);
      case "trips":
        return renderTripCell(column, row);
      case "stops":
        return renderStopCell(column, row);
      case "stopsArray":
        return renderStopsArrayCell(column, row);
      case "vehicles":
        return renderVehicleCell(column, row);
      case "drivers":
        return renderDriverCell(column, row);
      case "attendance":
        return renderAttendanceCell(column, row);
      case "scheduling":
        return renderScheduleCell(column, row);
      case "dispatch":
        return renderDispatchCell(column, row);
      case "associatedStudent":
        return renderChildCell(column, row);
      case "audit":
        return renderAuditCell(column, row);
      case "serviceAnnouncement":
        return renderServiceAnnouncementCell(column, row);
      case "busPolicy":
        return renderBusPolicyCell(column, row);
      case "generalPolicy":
        return renderBusPolicyCell(column, row);
      default:
        return row[column] ?? "N/A";
    }
  };

  const renderAdminCell = (column, row) => {
    switch (column) {
      case "fullName":
        return `${row.firstName} ${row.lastName}` || "N/A";
      case "role":
        const badgeProps = getBadgeProps(row[column]);
        return (
          <Badge
            width="fit-content"
            text={formatCapitalize(row[column])}
            {...badgeProps}
          />
        );
      default:
        return row[column] ?? "N/A";
    }
  };

  const renderFamilyCell = (column, row) => {
    switch (column) {
      case "rowNumber":
        return `${row[column]}.`;
      case "accountType": {
        const raw = row.accountType ?? row.subscription?.accountType ?? "";
        const trimmed = String(raw).trim();
        const key = trimmed.toLowerCase();
        const known = ["trial", "standard", "premium", "restricted"].includes(key);
        const badgeColors = known
          ? getFamilyAccountTypeBadgeColors(key)
          : getFamilyAccountTypeBadgeColors("");
        const text = trimmed ? getFamilyAccountTypeDisplay(trimmed) : "Unspecified";
        const sub = row.subscription || {};
        const planUpper = String(sub.plan || "").toUpperCase();
        const isRestricted =
          planUpper === "RESTRICTED" || key === "restricted";
        const formatSubscriptionDateOnly = (iso) => {
          if (!iso) return "—";
          const { formattedDate, formattedTime } = formatDateTime(iso, timezone);
          if (formattedDate === "-" && formattedTime === "-") return "—";
          return formattedDate;
        };
        const inGracePeriod = isFamilySubscriptionInGracePeriod(sub);
        const subscriptionTooltip =
          !isRestricted
            ? `<div class="app-tooltip-subscription">
                <div class="app-tooltip-row">
                  <span class="app-tooltip-label">Valid until</span>
                  <span class="app-tooltip-value">${formatSubscriptionDateOnly(sub.validUntil)}</span>
                </div>
                <div class="app-tooltip-row">
                  <span class="app-tooltip-label">Grace ends</span>
                  <span class="app-tooltip-value">${formatSubscriptionDateOnly(sub.graceEndsAt)}</span>
                </div>
              </div>`
            : undefined;
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.35rem",
              flexWrap: "wrap",
              width: "100%",
              boxSizing: "border-box",
              padding: "0.25rem 0",
            }}
          >
            <Badge
              width="fit-content"
              text={text}
              background={badgeColors.background}
              color={badgeColors.color}
              {...(subscriptionTooltip && {
                tooltip: subscriptionTooltip,
                showInfoDot: true,
              })}
            />
            {inGracePeriod && (
              <>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: "0.3rem",
                    cursor: "help",
                    lineHeight: "1",
                    height: "1.35rem",
                    padding: "0 0.45rem 0 0.35rem",
                    borderRadius: "999px",
                    backgroundColor: "#FFFAEB",
                    border: "1px solid #F79009",
                    color: "#B54708",
                    fontSize: "0.72rem",
                    fontWeight: 600,
                  }}
                  aria-label="Account in grace period"
                >
                  <img
                    src={WarningYellowIcon}
                    alt=""
                    width={11}
                    height={11}
                    style={{
                      display: "block",
                      flexShrink: 0,
                      alignSelf: "center",
                    }}
                  />
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      lineHeight: 1,
                      transform: "translateY(1px)",
                    }}
                  >
                    Grace Period
                  </span>
                </span>
              </>
            )}
          </div>
        );
      }
      case "parent":
        return row[column].map((parent, index) => {
          const sendEmailStatus = parent.emailOnboardStatus || 'sent';
          return (
            <div
              key={index}
              style={{ marginTop: "0.5rem", marginBottom: "0.5rem" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <div>{`${parent.firstName} ${parent.lastName}`}</div>
                <img
                  src={sendEmailStatus === 'sent' ? EmailSentIcon : sendEmailStatus === 'hold' ? EmailHoldIcon : sendEmailStatus === 'pending' ? EmailPendingIcon : sendEmailStatus === 'failed' ? EmailFailedIcon : EmailSentIcon}
                  alt={sendEmailStatus === 'sent' ? "Email Sent" : sendEmailStatus === 'hold' ? "Email Hold" : sendEmailStatus === 'pending' ? "Email Pending" : sendEmailStatus === 'failed' ? "Email Failed" : "Email Sent"}
                />
              </div>
              <div style={{ fontSize: "0.85rem", color: "#667085" }}>
                {`+${parent.callingCode}${parent.phoneNumber}`}
              </div>
            </div>
          );
        });

      case "child":
        const childrenData = row.children || [];
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
              padding: "0.5rem 0",
            }}
          >
            {childrenData.map((child, index) => (
              <FamilyChildCard key={index} child={child} />
            ))}
          </div>
        );

      case "address":
        const firstParent = row.parent?.[0];
        return (
          <div
            style={{
              fontSize: "0.875rem",
              whiteSpace: "normal",
              maxWidth: "15rem",
            }}
          >
            {firstParent?.address ?? "N/A"}
          </div>
        );

      default:
        return row[column] ?? "N/A";
    }
  };

  const renderChildCell = (column, row) => {
    switch (column) {
      case "stopName": {
        const childStops = getChildStopsFromRow(row);
        if (!childStops.length) return "-";

        const sortedStops = childStops.slice().sort((a, b) => {
          if (a.session === b.session) return 0;
          if (a.session === "none") return -1;
          if (b.session === "none") return 1;

          const aIndex = tripTypes?.findIndex((t) => t.id === a.session) ?? -1;
          const bIndex = tripTypes?.findIndex((t) => t.id === b.session) ?? -1;

          return (
            (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex)
          );
        });

        if (module === "associatedStudent") {
          return (
            <div>
              {sortedStops.map((stop, idx) => {
                const tripType = tripTypes?.find((t) => t.id === stop.session);
                const sessionLabel =
                  stop.session === "none"
                    ? "All"
                    : tripType?.name || stop.session;

                return (
                  <div
                    key={idx}
                    style={{ marginTop: "0.5rem", marginBottom: "0.5rem" }}
                  >
                    <div>{stop.name || "Unknown Stop"}</div>
                    <div style={{ fontSize: "0.85rem", color: "#667085" }}>
                      {`Trip Session: ${sessionLabel}`}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        }

        return (
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              flexWrap: "wrap",
              alignItems: "center",
              fontSize: "0.85rem",
              color: "#667085",
            }}
          >
            {sortedStops.map((stop, idx) => {
              const tripType = tripTypes?.find((t) => t.id === stop.session);
              const tooltipText =
                stop.session !== "none"
                  ? tripType?.name || stop.session
                  : undefined;

              const { background, color } = getTripBadgeProps(
                stop.session,
                tripTypes || [],
              );

              return (
                <span
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.25rem",
                  }}
                >
                  <Badge
                    text={stop.name}
                    background={background}
                    color={color}
                    width="fit-content"
                    tooltip={tooltipText}
                  />
                </span>
              );
            })}
          </div>
        );
      }
      case "fullName":
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.3rem",
            }}
          >
            {`${row.firstName} ${row.lastName}`}
            {row.alightPermission && (
              <img
                src={AlightPermissionIcon}
                alt="Permission Icon"
                style={{ height: "1.25rem", verticalAlign: "middle" }}
              />
            )}
          </span>
        );
      case "tripSession":
        const tripOptions = [
          { label: "All", value: "none" },
          ...tripTypes.map((trip) => ({
            label: trip.name,
            value: trip.id,
          })),
        ];

        return (
          <div style={{ maxWidth: "8rem" }}>
            <Dropdown
              options={tripOptions}
              name="tripSession"
              placeholder="Select"
              onChangeFunction={onRowChange(row.id)}
              input={{ tripSession: row.tripSession }}
              width="8rem"
            />
          </div>
        );
      default:
        return row[column] || "-";
    }
  };

  const renderParentCell = (column, row) => {
    switch (column) {
      case "fullName":
        return `${row.firstName} ${row.lastName}`;
      case "status":
        const badgeProps = getBadgeProps(row[column]);

        return (
          <Badge
            width="4rem"
            text={formatCapitalize(row[column])}
            {...badgeProps}
          />
        );
      case "emailOnboardStatus":
        const sendEmailOnboard = row.emailOnboardStatus || 'sent';
        return (
          <img
            src={sendEmailOnboard === 'sent' ? EmailSentIcon : sendEmailOnboard === 'hold' ? EmailHoldIcon : sendEmailOnboard === 'pending' ? EmailPendingIcon : sendEmailOnboard === 'failed' ? EmailFailedIcon : EmailSentIcon}
            alt={sendEmailOnboard === 'sent' ? "Email Sent" : sendEmailOnboard === 'hold' ? "Email Hold" : sendEmailOnboard === 'pending' ? "Email Pending" : sendEmailOnboard === 'failed' ? "Email Failed" : "Email Sent"}
          />
        );
      default:
        return row[column] ?? "N/A";
    }
  };

  const renderNotificationCell = (column, row) => {
    switch (column) {
      case "scheduledTime": {
        const { formattedDate, formattedTime } = formatDateTime(
          row?.alertTime?.scheduled?.datetime, timezone
        );
        return row?.alertTime?.scheduled?.isEnabled ? (
          <>
            <Text
              text={formattedDate}
              size="0.875rem"
              weight="400"
              height="1.5rem"
            />
            <Text text={formattedTime} size="0.75rem" weight="400" />
          </>
        ) : (
          "-"
        );
      }
      case "lastTriggered": {
        const { formattedDate, formattedTime } = formatDateTime(
          row?.lastTriggered, timezone
        );

        return row?.lastTriggered ? (
          <>
            <Text
              text={formattedDate}
              size="0.875rem"
              weight="400"
              height="1.5rem"
            />
            <Text text={formattedTime} size="0.75rem" weight="400" />
          </>
        ) : (
          "-"
        );
      }
      case "status":
        const badgeProps = getBadgeProps(row[column]);
        return (
          <Badge
            width="6rem"
            text={formatCapitalize(row[column])}
            {...badgeProps}
          />
        );
      case "targetAudience":
        return renderTargetAudienceCell(row);
      default:
        return row[column] ?? "N/A";
    }
  };

  const renderTripCell = (column, row) => {
    switch (column) {
      case "status": {
        const badgeProps = getBadgeProps(row[column]);
        return (
          <Badge
            width="4rem"
            text={formatCapitalize(row[column])}
            {...badgeProps}
          />
        );
      }
      case "stopsAmount": {
        const badgeProps = getBadgeProps(row[column]);
        return (
          <Badge width="1rem" text={String(row[column])} {...badgeProps} />
        );
      }
      case "totalStudents": {
        const badgeProps = getBadgeProps(row[column]);
        return (
          <Badge width="1rem" text={String(row[column])} {...badgeProps} />
        );
      }
      case "type": {
        const value = row[column];
        const matchedType = tripTypes.find((type) => type.id === value);
        const showMissingTripTypeWarning = Boolean(value) && !matchedType;
        const displayText = resolveTripTypeDisplayName(row, matchedType?.name);

        return (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.35rem",
            }}
          >
            <Badge
              width="fit-content"
              text={displayText}
              {...getTripBadgeProps(value, tripTypes)}
            />
            {showMissingTripTypeWarning && (
              <span
                className="trip-type-mismatch-icon-trigger"
                data-tooltip-id={APP_ERROR_TOOLTIP_ID}
                data-tooltip-html={TRIP_TYPE_MISMATCH_TOOLTIP_HTML}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "1.125rem",
                  height: "1.125rem",
                  borderRadius: "999px",
                  background: "#FEF3F2",
                  color: "#B42318",
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  lineHeight: 1,
                  cursor: "help",
                  flexShrink: 0,
                }}
                aria-label="Trip type no longer exists for the institution"
              >
                !
              </span>
            )}
          </div>
        );
      }
      case "startTime":
        return formatTo12HourTime(row[column]);
      default:
        return row[column] ?? "N/A";
    }
  };

  const renderStopCell = (column, row) => {
    switch (column) {
      case "name":
        return (
          <div
            style={{
              whiteSpace: "normal",
              maxWidth: "20vw",
            }}
          >
            {row[column] || "N/A"}
          </div>
        );

      case "status": {
        const badgeProps = getBadgeProps(row[column]);
        return (
          <Badge
            width="4rem"
            text={formatCapitalize(row[column])}
            {...badgeProps}
          />
        );
      }
      case "totalChildCount":
        const stopChildren = Array.isArray(row.associatedChilds)
          ? row.associatedChilds
          : Array.isArray(row.children)
            ? row.children
            : [];
        const childNames = stopChildren
          .map((child) => {
            const preferredName = child?.preferredName?.trim();
            if (preferredName) return preferredName;
            const fullName = `${child?.firstName || ""} ${child?.lastName || ""}`.trim();
            return fullName || null;
          })
          .filter(Boolean);
        const totalStudentsBadgeProps = getBadgeProps(row[column]);
        const studentCountText = String(row[column] ?? 0);

        if (childNames.length === 0) {
          return (
            <div>
              <Badge
                width="1rem"
                text={studentCountText}
                {...totalStudentsBadgeProps}
              />
            </div>
          );
        }

        const tooltipHtml = `
          <div class="app-tooltip-title">Associated Students</div>
          <ol>
            ${childNames.map((name) => `<li>${name}</li>`).join("")}
          </ol>
        `;

        return (
          <div
            data-tooltip-id={APP_TOOLTIP_ID}
            data-tooltip-html={tooltipHtml}
            style={{ display: "inline-flex", alignItems: "center" }}
          >
            <Badge
              width="1rem"
              text={studentCountText}
              {...totalStudentsBadgeProps}
            />
          </div>
        );
      case "address":
        return (
          <div
            style={{
              whiteSpace: "normal",
              maxWidth: "20rem",
            }}
          >
            {row[column] || "N/A"}
          </div>
        );
      default:
        return row[column] ?? "N/A";
    }
  };

  const renderStopsArrayCell = (column, row) => {
    switch (column) {
      case "name":
        return (
          <div
            style={{
              whiteSpace: "normal",
              maxWidth: "9rem",
            }}
          >
            {row[column] || "N/A"}
          </div>
        );
      case "children":
        const visibleChildren =
          row.associatedChilds?.filter((child) => child.selected !== false) ||
          [];

        return visibleChildren.length > 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              whiteSpace: "normal",
              maxWidth: "20rem",
            }}
          >
            {visibleChildren.map((child, index) => (
              <span key={child.childId || index}>
                {index + 1}. {child.firstName} {child.lastName}
              </span>
            ))}
          </div>
        ) : (
          <span>-</span>
        );
      case "sequence":
        const badgeProps = getBadgeProps(row[column]);
        return (
          <div style={{ marginLeft: "-2rem" }}>
            <Badge width="1rem" text={String(row[column])} {...badgeProps} />
          </div>
        );
      case "eta":
        return (
          <div>
            <CustomTimePicker
              name="eta"
              value={row.eta}
              onChange={(val) => onRowChange(rowId, "eta", val)}
              width="fit-content"
              placeholder="Select Time"
              disabled={role === "VIEWER"}
            />
          </div>
        );
      default:
        return row[column] ?? "N/A";
    }
  };

  const renderVehicleCell = (column, row) => {
    switch (column) {
      case "status": {
        const badgeProps = getBadgeProps(row[column]);
        return (
          <Badge
            width="4rem"
            text={formatCapitalize(row[column])}
            {...badgeProps}
          />
        );
      }
      case "capacity": {
        const badgeProps = getBadgeProps(row[column]);
        return (
          <Badge
            width="fit-content"
            text={String(row[column])}
            {...badgeProps}
          />
        );
      }
      default:
        return row[column] ?? "-";
    }
  };

  const renderDriverCell = (column, row) => {
    switch (column) {
      case "status": {
        const badgeProps = getBadgeProps(row[column]);
        return (
          <Badge
            width="4rem"
            text={formatCapitalize(row[column])}
            {...badgeProps}
          />
        );
      }
      case "type": {
        const badgeProps = getBadgeProps(row[column]);
        return (
          <Badge
            width="4rem"
            text={formatCapitalize(row[column])}
            {...badgeProps}
          />
        );
      }
      case "phoneNumber":
        return addPlusPhone(row[column]);
      default:
        return row[column] ?? "N/A";
    }
  };

  const renderAttendanceCell = (column, row) => {
    switch (column) {
      case "class":
        return row[column] || "-";
      case "stopName":
        return row[column] || "-";
      case "exempted":
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <Checkbox
              checked={row.status === "Exempted"}
              onChange={() => handlePopoverAction(row.childId)}
              disabled={role === "VIEWER"}
            />
          </div>
        );
      case "status":
        const status = row[column];

        const badgeProps = getBadgeProps(status);

        let displayText;
        switch (status) {
          case "pickUp":
            displayText = "Picked Up";
            break;
          case "dropOff":
            displayText = "Dropped Off";
            break;
          case "failedToBoard":
            displayText = "Failed to Board";
            break;
          case "Exempted":
            displayText = "Exempted";
            break;
          case "Scheduled":
            displayText = "Scheduled";
            break;
          default:
            displayText = formatCapitalize(status);
        }

        return <Badge width="fit-content" text={displayText} {...badgeProps} />;

      default:
        return row[column] ?? "N/A";
    }
  };

  const renderScheduleCell = (column, row) => {
    switch (column) {
      case "name":
        return row.tripDetails?.tripName;
      case "tripType":
        const value = row.tripDetails?.tripType;
        const matchedType = tripTypes.find((type) => type.id === value);
        const displayText = resolveTripTypeDisplayName(row.tripDetails, matchedType?.name);
        return (
          <Badge
            width="fit-content"
            text={displayText || value}
            {...getTripBadgeProps(value, tripTypes)}
          />
        );
      case "startTime":
        return formatTo12HourTime(row.startTime);
      case "driver":
        return row.driverDetails?.driverName || "-";
      case "assistant":
        return row.assistantDetails?.assistantName || "-";
      case "vehicle": {
        const badgeProps = getBadgeProps(row.vehicleDetails?.capacity);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <span>{row.vehicleDetails?.vehicleNumber || "N/A"}</span>
            <Badge
              width="1.5rem"
              text={String(row.vehicleDetails?.capacity)}
              {...badgeProps}
            />
          </div>
        );
      }
      case "totalStudents": {
        const scheduleStatus = (row.status || "").toLowerCase();
        const summary = row.attendanceSummary || {};
        const getCount = (key) => String(summary[key] ?? 0);

        const renderPair = (leftText, leftKey, rightText, rightKey) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              flexWrap: "wrap",
            }}
          >
            <Badge
              width="fit-content"
              text={`${leftText}: ${getCount(leftKey)}`}
              {...getBadgeProps(leftKey)}
            />
            <Badge
              width="fit-content"
              text={`${rightText}: ${getCount(rightKey)}`}
              {...getBadgeProps(rightKey)}
            />
          </div>
        );

        const renderTriple = (
          firstText,
          firstKey,
          secondText,
          secondKey,
          thirdText,
          thirdKey
        ) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              flexWrap: "wrap",
            }}
          >
            <Badge
              width="fit-content"
              text={`${firstText}: ${getCount(firstKey)}`}
              {...getBadgeProps(firstKey)}
            />
            <Badge
              width="fit-content"
              text={`${secondText}: ${getCount(secondKey)}`}
              {...getBadgeProps(secondKey)}
            />
            <Badge
              width="fit-content"
              text={`${thirdText}: ${getCount(thirdKey)}`}
              {...getBadgeProps(thirdKey)}
            />
          </div>
        );

        if (scheduleStatus === "scheduled") {
          return renderPair("Scheduled", "Scheduled", "Exempted", "Exempted");
        }

        if (scheduleStatus === "ongoing") {
          return renderPair("Picked Up", "pickUp", "Failed", "failedToBoard");
        }

        if (scheduleStatus === "completed") {
          return renderTriple(
            "Dropped Off",
            "dropOff",
            "Failed",
            "failedToBoard",
            "Exempted",
            "Exempted"
          );
        }

        const badgeProps = getBadgeProps(row[column]);
        return <Badge width="1.5rem" text={String(row[column] ?? 0)} {...badgeProps} />;
      }
      case "status":
        const text = formatCapitalize(row[column]);
        const badgeProps = getBadgeProps(text);
        return <Badge width="fit-content" text={text} {...badgeProps} />;
      default:
        return row[column] ?? "N/A";
    }
  };

  const renderDispatchCell = (column, row) => {
    switch (column) {
      case "vehicleNumber":
        return row.vehicleDetails?.busNumber
          ? `${row.vehicleDetails?.vehicleNumber} (${row.vehicleDetails?.busNumber})`
          : row.vehicleDetails?.vehicleNumber;
      case "tripName":
        return row.tripName || "-";
      case "prevStopData":
        return row.previousStop?.name || "-";
      case "nextStopData":
        return row.nextStop?.name || "-";
      case "nextStopArrivalData": {
        const { formattedTime } = formatETATime(row.nextStop?.eta);
        return formattedTime || "-";
      }
      case "finalStopData": {
        return row.finalStop?.name || "-";
      }
      case "finalStopArrivalData": {
        const { formattedTime } = formatETATime(row.finalStop?.eta);
        return formattedTime || "-";
      }
      case "occupancyData": {
        return (
          <DispatchOccupancyCell
            occupancy={row.occupancy}
            tripStatus={row.status}
          />
        );
      }
      case "tripStatus": {
        const formattedValue = formatDispatchTripStatus(row.status);
        const badgeProps = getDispatchTripStatusBadgeProps(row.status);
        return (
          <Badge width="fit-content" text={formattedValue} {...badgeProps} /> ||
          "N/A"
        );
      }
      default:
        return row[column] ?? "N/A";
    }
  };

  const renderAuditCell = (column, row) => {
    switch (column) {
      case "module": {
        const rawModule = row[column];

        const displayModule = rawModule === "childs" ? "children" : rawModule;

        return formatCapitalize(displayModule) || "N/A";
      }

      case "actionType": {
        const text = formatCapitalize(row[column]);
        const badgeProps = getBadgeProps(row[column]);
        return <Badge width="fit-content" text={text} {...badgeProps} />;
      }

      case "user":
        return <>
            <Text
              text={row.performedBy?.name}
              size="0.875rem"
              weight="400"
              height="1.5rem"
            />
            <Text text={formatCapitalize(row.performedBy?.role)} size="0.75rem" weight="400" />
          </>;

      case "time": {
        const { formattedDate, formattedTime } = formatDateTime(row.timestamp, timezone);
        return (
          <>
            <Text
              text={formattedDate}
              size="0.875rem"
              weight="400"
              height="1.5rem"
            />
            <Text text={formattedTime} size="0.75rem" weight="400" />
          </>
        );
      }

      case "fieldsChanged": {
        const { primary, secondary } = getAuditDetailParts(row);

        if (!secondary) {
          return (
            <Text
              text={primary}
              size="0.875rem"
              weight="400"
              height="1.5rem"
              color={primary === "—" ? "#667085" : undefined}
            />
          );
        }

        return (
          <>
            <Text
              text={primary}
              size="0.875rem"
              weight="400"
              height="1.5rem"
            />
            <Text
              text={secondary}
              size="0.75rem"
              weight="400"
              color="#667085"
            />
          </>
        );
      }

      default:
        return row[column] ?? "N/A";
    }
  };

  const serviceIcons = {
    alert: AlertIcon,
    checkMark: CheckIcon,
    info: InfoIcon,
    arrow: ArrowIcon,
    clock: ClockIcon,
    star: StarIcon,
  };

  const getServiceIconImage = (icon) => serviceIcons[icon] || null;

  const getTitleColorBadgeProps = (hex) => {
    switch (hex) {
      case "#099250": // Green
        return { background: "#ECFDF3", color: "#099250", label: "Green" };
      case "#FABE00": // Yellow
        return { background: "#FFFAEB", color: "#FABE00", label: "Yellow" };
      case "#B42318": // Red
        return { background: "#FEF3F2", color: "#B42318", label: "Red" };
      case "#175CD3": // Blue
        return { background: "#EFF8FF", color: "#175CD3", label: "Blue" };
      default:
        return { background: "#F2F4F7", color: "#344054", label: "Unknown" };
    }
  };

  const renderServiceAnnouncementCell = (column, row) => {
    switch (column) {
      case "title":
        return row[column] ?? "No Title Specified";

      case "icon":
        const Icon = getServiceIconImage(row[column]);
        return Icon ? <img src={Icon} alt={row[column]} /> : null;
      case "titleColor":
        const badgeProps = getTitleColorBadgeProps(row[column]);
        return <Badge width="4rem" text={badgeProps.label} {...badgeProps} />;
      case "content":
        return (
          <div
            style={{
              maxWidth: "10rem",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {truncateText(row[column], 70)}
          </div>
        );
      case "status": {
        const status = row.isActive ? "active" : "inactive";
        const badgeProps = getBadgeProps(status);

        return (
          <Badge width="4rem" text={formatCapitalize(status)} {...badgeProps} />
        );
      }
      default:
        return row[column] ?? "";
    }
  };

  const renderBusPolicyCell = (column, row) => {
    switch (column) {
      case "title":
        return row[column] ?? "No Title Specified";

      case "version":
        const badgeProps = getBadgeProps(row[column]);
        return <Badge width="fit-content" text={row[column]} {...badgeProps} />;
      case "effectiveDate":
        if (!row[column]) return "Not Specified";
        const { formattedDate } = formatDateTime(row[column], timezone);
        return formattedDate || "Not Specified";
      case "status": {
        const status = row.isActive ? "active" : "inactive";
        const badgeProps = getBadgeProps(status);

        return (
          <Badge width="4rem" text={formatCapitalize(status)} {...badgeProps} />
        );
      }
      default:
        return row[column] ?? "";
    }
  };

  const handlePopoverToggle = () => {
    setIsPopoverOpen(!isPopoverOpen);
    onPopoverToggle(rowId);
  };

  const currentEmail = Cookies.get("email");
  const isOwnAccount = currentEmail === row.email;

  const isSuperAdmin = role === "SUPERADMIN";
  const isAdmin = role === "ADMIN";
  const isEditorOrViewer = role === "EDITOR" || role === "VIEWER";

  const isRowAdmin = row.role === "ADMIN";
  const isRowSuperAdmin = row.role === "SUPERADMIN";

  const canEditOrDeleteAdmin =
    module === "admin" &&
    !isOwnAccount &&
    (isSuperAdmin ||
      (isAdmin && !isRowAdmin && !isRowSuperAdmin));

  const canViewOnlyAdmin =
    module === "admin" &&
    (isOwnAccount ||
      isEditorOrViewer ||
      (isAdmin && isRowAdmin));

  const shouldShowMenu =
    module !== "attendance" &&
    module !== "dispatch" &&
    module !== "audit" &&
    !(
      role === "VIEWER" &&
      (module === "stopsArray" || module === "associatedStudent")
    );

  const rowClickable =
    onRowClick && (!isRowClickable || isRowClickable(row));

  return (
    <tr
      ref={setNodeRef}
      style={style}
      {...attributes}
      onClick={rowClickable ? () => onRowClick(row) : undefined}
      className={rowClickable ? "clickable-row" : ""}
    >
      {draggable && (
        <td {...listeners}>
          <img
            src={DragIcon}
            alt="drag"
            style={{
              verticalAlign: "middle",
            }}
          />
        </td>
      )}
      {columns.map((column, index) => (
        <td
          key={index}
          className={module === "dispatch" ? `table-col table-col--${column}` : undefined}
        >
          {renderCellData(column)}
        </td>
      ))}
      {shouldShowMenu && (
        <td className="table-menu">
          <TablePopover
            isOpen={openPopover === rowId}
            onClose={() => onPopoverToggle(null)}
            rowId={rowId}
            row={row}
            handleAction={handlePopoverAction}
            module={module}
            canEdit={canEditOrDeleteAdmin}
            canView={canViewOnlyAdmin}
          >
            <button className="table-menu-button" onClick={handlePopoverToggle}>
              <img src={MenuIcon} alt="Menu Icon" className="table-menu-icon" />
            </button>
          </TablePopover>
        </td>
      )}
    </tr>
  );
}
